// i18n
import { translateInComponent } from 'infras/i18n';
import { post } from 'infras/rest_client';
import {
  SyntheticEvent,
  DragEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import translations from './i18n.json';
import { isUrl } from 'infras/validator';

interface Props {
  imageAllowedTypes: string[];
  imageUploadUrl?: string;
  fileUploadUrl?: string;
  onUploadError?: (errorMessage: string) => void;
}

export const useRichEditor = (props: Props) => {
  const t = translateInComponent(translations);
  const editorRef = useRef<any>(null);
  const [isShowLinkDialog, setIsShowLinkDialog] = useState<boolean>(false);
  const [linkText, setLinkText] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [linkInsertPosition, setLinkInsertPosition] = useState<{
    index: number;
    length: number;
  } | null>(null);

  const insertImageToEditor = (url: string) => {
    if (editorRef.current) {
      const editor = editorRef.current.getEditor();
      const index = editorRef.current?.selection?.index ?? 0;
      editor.insertEmbed(index, 'image', url);
    }
  };

  const insertFileToEditor = (url: string, name: string) => {
    if (editorRef.current) {
      const editor = editorRef.current.getEditor();
      const index = editorRef.current?.selection?.index ?? 0;
      editor.insertText(index, name, 'link', url);
    }
  };

  const insertLinkToEditor = () => {
    if (editorRef.current && linkUrl && linkText) {
      const editor = editorRef.current.getEditor();
      const index = linkInsertPosition?.index ?? 0;
      const url = isUrl(linkUrl) ? linkUrl : `https://${linkUrl}`;

      editor.insertText(index, linkText, 'link', url);
      editor.setSelection(index + linkText.length);
    }
  };

  const uploadImage = async (file: File) => {
    if (!props.imageUploadUrl) {
      return;
    }
    const regex = /\/(.*?)$/;
    const ext = file.type.match(regex);
    if (ext && ext.length > 0 && props.imageAllowedTypes.includes(ext[1])) {
      const data = new FormData();
      data.set('file', file);
      const result = await post(props.imageUploadUrl, { data });
      return result;
    }
  };

  const onDragFile = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.files || event.dataTransfer.files.length === 0) {
      props.onUploadError && props.onUploadError(t('ErrorImageUpload'));
      return;
    }
    event.stopPropagation();
    event.preventDefault();

    const files = event.dataTransfer.files;
    const file = files[0];
    const upload = async () => {
      const result = await uploadImage(file);
      if (!result || !result.isSuccess) {
        props.onUploadError && props.onUploadError(t('ErrorImageUpload'));
        return;
      }
      const imageLink = result.data.link;
      insertImageToEditor(imageLink);
    };
    upload();
  }, []);

  const onClickLinkTool = useCallback(() => {
    setIsShowLinkDialog(true);
    if (editorRef.current) {
      setLinkInsertPosition(editorRef.current.selection ?? 0);
    }
  }, []);

  const onClickImageUploadTool = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.click();
    input.onchange = async () => {
      if (input.files) {
        const file = input.files[0];
        const result = await uploadImage(file);
        if (!result || !result.isSuccess) {
          props.onUploadError && props.onUploadError(t('ErrorImageUpload'));
          return;
        }
        const imageLink = result.data.link;
        insertImageToEditor(imageLink);
      }
    };
  }, [props.imageUploadUrl]);

  const onClickFileUploadTool = useCallback(() => {
    if (!props.fileUploadUrl) {
      return;
    }
    const fileUploadUrl = props.fileUploadUrl;
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.click();
    input.onchange = async () => {
      if (input.files) {
        const file = input.files[0];

        const data = new FormData();
        data.set('file', file);
        const result = await post(fileUploadUrl, { data });
        if (!result.isSuccess) {
          props.onUploadError && props.onUploadError(t('ErrorFileUpload'));
          return;
        }
        const { link, originalName } = result.data;
        insertFileToEditor(link, originalName);
      }
    };
  }, [props.fileUploadUrl]);

  const onCancelLinkDialog = useCallback(() => {
    if (editorRef.current) {
      const editor = editorRef.current.getEditor();
      editor.setSelection(linkInsertPosition?.index ?? 0);
    }
    setIsShowLinkDialog(false);
    setLinkText(null);
    setLinkUrl(null);
    setLinkInsertPosition(null);
  }, []);

  const onSubmitLinkDialog = useCallback(() => {
    insertLinkToEditor();
    setIsShowLinkDialog(false);
    setLinkText(null);
    setLinkUrl(null);
    setLinkInsertPosition(null);
  }, [linkText, linkUrl]);

  const onInputChange = useCallback((e: SyntheticEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    switch (name) {
      case 'linkUrl':
        setLinkUrl(value);
        break;
      case 'linkText':
        setLinkText(value);
        break;
      default:
        break;
    }
  }, []);

  return {
    state: {
      editorRef,
      isShowLinkDialog,
      linkText,
      linkUrl,
    },
    actions: {
      insertImageToEditor,
      onClickLinkTool,
      onClickImageUploadTool,
      onClickFileUploadTool,
      onDragFile,
      onSubmitLinkDialog,
      onCancelLinkDialog,
      onInputChange,
    },
  };
};
